import React, { useEffect } from "react";
import BiogEducation from "./BiogEducation";
import BiogGrants from "./BiogGrants";
import BiogExhibits from "./BiogExhibits";
import BiogArticles from "./BiogArticles";
import BiogCollections from "./BiogCollections";
import BiogTeaching from "./BiogTeaching";
import BiogCatalogs from "./BiogCatalogs";
import BiogProfExperience from "./BiogProfExperience";
import useSharedDocHeader from "./DocHeaderHook";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import createTheme from "@mui/material/styles/createTheme";
import MuiAccordion from "@mui/material/Accordion";
import styled from "@mui/material/styles/styled";
import  Stack from "@mui/material/Stack";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import { noraWebTheme } from "./noraWebTheme";
import BiogArtOrganizations from "./BiogArtOrganizations";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

const biographyQuery = () => ({
  queryKey: ["biography"],
  queryFn: async () =>
    fetch("/data/biography.json", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }).then((res) => res.json()),
  option: { staleTime: 1000 * 60 * 5 },
});

export const loader =
  (queryClient) =>
  async ({}) => {
    const query = biographyQuery();
    // ⬇️ return data or fetch it
    return (
      queryClient.getQueryData(query.queryKey) ??
      (await queryClient.fetchQuery(query))
    );
  };

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: 0,
  margin:0,
  padding:0,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

export default function Biography() {
  const { doSetTitleKey, doSetMetaDescKey, doSetRouterId } =
    useSharedDocHeader();
  const [expanded, setExpanded] = React.useState(false);
  const { t } = useTranslation("common");

  const { isLoading, isSuccess, error, data } = useQuery(biographyQuery());

  useEffect(() => {
    doSetTitleKey("common:LABEL_BIOGRAPHY");
    doSetMetaDescKey("common:LABEL_DESC_BIOGRAPHY");
    doSetRouterId("/Biography");
  }, []);

  if (isLoading) {
    return "Loading...";
  }

  if (error) {
    return "An error has occurred: " + error.message;
  }

  const tableTheme = createTheme(noraWebTheme, {
    components: {
      // Name of the component
      MuiTableCell: {
        styleOverrides: {
          // Name of the slot
          root: {
            // Some CSS
            [noraWebTheme.breakpoints.down("md")]: {
              fontSize: "1rem",
            },
            [noraWebTheme.breakpoints.up("md")]: {
              fontSize: "1.2rem",
            },
          },
        },
      },
    },
  });

  const outer = {
    border: "0",
    color: "black",
    fontSize: "25px",
    alignItems: "center",
    fontWeight: "bold",
    flexGrow: 1,
    backgroundColor: "background.paper",
    mt: { xs: 1, sm: 5 },
  };
  const inner = {
    pl: 3,
    alignItems: "top",
    justifyContent: "center",
    flexGrow: 1,
    bgcolor: "background.paper",
  };
  const accordionSummaryStyle = {
    flexDirection: "row-reverse",
  };
  const headingStyle = {
    fontWeight: "bold",
    flexShrink: 0,
    textDecorationLine: "underline",
    width: "90%",
    ml: 2,
  };
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (isLoading) return <Box>Loading...</Box>;

  if (isSuccess) {
    return (
      <ThemeProvider theme={tableTheme}>
        <Grid2
          height="100vh"
          container
          display="flex"
          spacing={2}
          flexDirection="column"
          sx={outer}
        >
          <Grid2
            width="100vw"
            display="flex"
            flexDirection="row"
            spacing={2}
            sx={inner}
          >
            <Stack sx={{ bgcolor: "background.paper", pt: 3 }}>
              <Accordion
                disableGutters={true}
                expanded={expanded === "panelEDU"}
                onChange={handleChange("panelEDU")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel0bh-content"
                  id="panel0bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_ART_EDUCATION") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogEducation />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                expanded={expanded === "panelSINGLE"}
                onChange={handleChange("panelSINGLE")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_SINGLE_EXHIB") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogExhibits
                    exhibitionList={data.singleExhibitions}
                    exhibitLabel="common:LABEL_SINGLE_EXHIB"
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                disableGutters={true}
                expanded={expanded === "panelGROUP"}
                onChange={handleChange("panelGROUP")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2bh-content"
                  id="panel2bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_GROUP_EXHIB") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogExhibits
                    exhibitionList={data.groupExhibitions}
                    exhibitLabel="common:LABEL_GROUP_EXHIB"
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                disableGutters={true}
                expanded={expanded === "panelGRANT"}
                onChange={handleChange("panelGRANT")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel3bh-content"
                  id="panel3bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_GRANTS") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogGrants />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                expanded={expanded === "panelCOLL"}
                onChange={handleChange("panelCOLL")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel4bh-content"
                  id="panel4bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_COLLECTIONS") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogCollections />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                expanded={expanded === "panelARTICLES"}
                onChange={handleChange("panelARTICLES")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel5bh-content"
                  id="panel5bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_ARTICLES") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogArticles
                    articles={data.articles}
                    articleLabel="common:LABEL_ARTICLES"
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                expanded={expanded === "panelCATELOG"}
                onChange={handleChange("panelCATELOG")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel6bh-content"
                  id="panel6bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_CATELOGS") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogCatalogs
                    catalogs={data.catalogs}
                    catalogLabel="common:LABEL_CATELOGS"
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                expanded={expanded === "panelORG"}
                onChange={handleChange("panelORG")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel7bh-content"
                  id="panel7bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_ORGANISATIONS") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogArtOrganizations />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                expanded={expanded === "panelLECT"}
                onChange={handleChange("panelLECT")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel8bh-content"
                  id="panel8bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_LECTURES") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogTeaching />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                expanded={expanded === "panelEXPER"}
                onChange={handleChange("panelEXPER")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel9bh-content"
                  id="panel9bh-header"
                  sx={accordionSummaryStyle}
                >
                  <Typography variant="h6" sx={headingStyle}>
                    {t("common:LABEL_EXPERIENCE") + ":"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiogProfExperience />
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Grid2>
        </Grid2>
      </ThemeProvider>
    );
  }
}
